import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Eye } from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/utils";

function Badge({ product }) {
  if (!product.badge) return null;
  const map = {
    discount: "bg-obsidian text-gold border border-gold/40",
    new: "bg-gold-gradient text-obsidian",
    featured: "bg-white/90 text-obsidian border border-obsidian/10",
  };
  return (
    <span
      data-testid={`product-badge-${product.id}`}
      className={`absolute left-4 top-4 z-20 rounded-full px-3 py-1 text-[10px] font-accent font-semibold uppercase tracking-widest ${map[product.badgeType] || map.featured}`}
    >
      {product.badge}
    </span>
  );
}

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -6, y: px * 8 });
  };
  const reset = () => setTilt({ x: 0, y: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={reset}
        className="preserve-3d relative rounded-[1.4rem] bg-card p-3 shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-luxe"
        style={{
          transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          border: "1px solid hsl(var(--border))",
        }}
      >
        <div className="relative overflow-hidden rounded-[1.1rem]">
          <Badge product={product} />
          {!product.inStock && (
            <span className="absolute right-4 top-4 z-20 rounded-full bg-obsidian/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              Épuisé
            </span>
          )}
          <Link to={`/produit/${product.id}`} aria-label={product.name}>
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
            />
          </Link>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          <div className="absolute inset-x-3 bottom-3 z-20 flex translate-y-4 gap-2 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
            <button
              data-testid={`quick-add-${product.id}`}
              onClick={() => addItem(product, { quantity: 1 })}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-obsidian py-2.5 text-xs font-semibold text-cream transition-colors hover:bg-gold hover:text-obsidian"
            >
              <ShoppingBag size={15} /> Ajouter
            </button>
            <button
              data-testid={`quick-view-${product.id}`}
              onClick={() => navigate(`/produit/${product.id}`)}
              className="flex items-center justify-center rounded-full bg-cream/95 px-3.5 py-2.5 text-obsidian transition-colors hover:bg-gold"
              aria-label="Voir les détails"
            >
              <Eye size={16} />
            </button>
          </div>
        </div>

        <div className="px-2 pb-2 pt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{product.category}</span>
            <StarRating rating={product.rating} size={12} showValue count={product.reviewsCount} />
          </div>
          <Link to={`/produit/${product.id}`}>
            <h3 className="font-serif text-xl font-medium leading-tight text-foreground transition-colors group-hover:text-gold-dark">
              {product.name}
            </h3>
          </Link>
          <div className="mt-2 flex items-end gap-2">
            <span className="text-lg font-semibold text-foreground">{formatPrice(product.price)}</span>
            {product.oldPrice && (
              <span className="mb-0.5 text-sm text-muted-foreground line-through">{formatPrice(product.oldPrice)}</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
